import { type SupportedLocale } from '@zep-us/i18n';
import axios from 'axios';

import { isProduction } from '@/consts/env';

const WEEGLOO_SPACE_ID = process.env.NEXT_PUBLIC_WEEGLOO_SPACE_ID?.trim();
const WEEGLOO_CDN_TOKEN = process.env.NEXT_PUBLIC_WEEGLOO_CDN_TOKEN?.trim();

const assertWeeglooConfig = () => {
  if (
    [WEEGLOO_SPACE_ID, WEEGLOO_CDN_TOKEN].some(
      value => !value || value === 'undefined' || value === 'null',
    )
  ) {
    throw new Error('Missing required Weegloo environment variables');
  }
};

/**
 * Weegloo CDA HTTP Instance (읽기 전용)
 * @description Content Delivery API - 퍼블리시된 콘텐츠 조회용
 * @see https://zep.us/weegloo/cda/v1/spaces/{SPACE_ID}/contents/
 */
const WEEGLOO_CDA_INSTANCE = axios.create({
  baseURL: `https://zep.us/weegloo/cda/v1/spaces/${WEEGLOO_SPACE_ID}/contents/`,
  headers: {
    Authorization: `Bearer ${WEEGLOO_CDN_TOKEN}`,
  },
});

/**
 * CDA API 응답 타입 (locale-unwrapped 형태)
 * CDA는 쿼리 파라미터로 locale을 받아서 해당 locale 데이터만 직접 반환합니다.
 */
type WeeglooContentCDAResponse<T = object> = {
  metadata: { tags: [] };
  fields: {
    json: T;
    jsonDev: T;
    title: string;
  };
  sys: {
    version: number;
  };
};

/**
 * Weegloo CDA에서 퍼블리시된 콘텐츠를 조회합니다.
 * @description Content Delivery API 사용 (읽기 전용)
 * @param entryId 요청할 Weegloo Content ID
 * @param locale 조회할 locale (optional, 기본값: en-US)
 * @returns {Promise<T>} 바로 데이터 반환
 */
export const getZepetofulCDNContent = async <T = object>({
  entryId,
  locale = 'en-US',
}: {
  entryId: string;
  locale?: SupportedLocale | 'en-US';
}): Promise<T> => {
  assertWeeglooConfig();

  const res = await WEEGLOO_CDA_INSTANCE.get<WeeglooContentCDAResponse<T>>(
    entryId,
    {
      params: {
        locale: locale === 'en' ? 'en-US' : locale,
        select: isProduction ? 'fields.json' : 'fields.jsonDev',
      },
    },
  );
  const jsonData = res.data.fields.json ?? res.data.fields.jsonDev;
  return replaceWeeglooCdnUrl(jsonData) as T;
};

export const replaceWeeglooCdnUrl = (data: unknown): unknown => {
  const CMS_CDN_BASE_URL = process.env.NEXT_PUBLIC_CMS_CDN_BASE_URL;

  if (typeof data === 'string') {
    if (!CMS_CDN_BASE_URL) return data;
    return data.replace(
      /https:\/\/(?:files\.weegloo\.com|weegloo-media\.com)/g,
      CMS_CDN_BASE_URL,
    );
  }

  if (Array.isArray(data)) {
    return data.map(item => replaceWeeglooCdnUrl(item));
  }

  if (data && typeof data === 'object') {
    return Object.fromEntries(
      Object.entries(data).map(([key, value]) => [
        key,
        replaceWeeglooCdnUrl(value),
      ]),
    );
  }

  return data;
};
